"use client";

import { useState, useRef, useEffect } from 'react';
import { useLocale, SUPPORTED_LOCALES } from '@/lib/locale-context';

function TranslateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
    </svg>
  );
}

export default function LanguageSwitcher({ onLocaleChange }: { onLocaleChange?: () => void }) {
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = SUPPORTED_LOCALES.find(l => l.code === locale) || SUPPORTED_LOCALES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white/90 backdrop-blur-xs hover:bg-slate-50 transition-colors text-sm text-slate-800 shadow-xs"
        aria-label="Change language"
      >
        <TranslateIcon className="w-4 h-4 text-slate-500" />
        <span className="font-medium">{current.label}</span>
        <span>{current.shortLabel}</span>
        <svg className={`w-3 h-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 min-w-[170px] bg-white rounded-lg border border-slate-100 shadow-lg py-1 z-50">
          {SUPPORTED_LOCALES.map((loc) => (
            <button
              key={loc.code}
              onClick={() => {
                if (loc.code !== locale) {
                  setLocale(loc.code);
                  onLocaleChange?.();
                }
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                locale === loc.code
                  ? 'bg-slate-50 text-slate-800 font-medium'
                  : 'text-slate-700 hover:bg-slate-50/50'
              }`}
            >
              <span>{loc.label}</span>
              <span className="text-base">{loc.shortLabel}</span>
              {locale === loc.code && (
                <svg className="w-4 h-4 ml-auto text-slate-700" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
